import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { UsersThree, IdentificationBadge } from "@phosphor-icons/react";
import { toast } from "sonner";
import { OpsOverview } from "../components/hr/OpsOverview";
import { TeamTable } from "../components/hr/TeamTable";
import { LeaveCalendar } from "../components/hr/LeaveCalendar";
import { dayRange, StatusPill, ukDate, money, monthLabel } from "../components/hr/hrUtils";

const TABS = ["overview", "team", "leave", "sickness", "invoices"];

/** Operations Director view of the whole admin team's HR. */
export default function HRHubPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const [overview, setOverview] = useState(null);
  const [team, setTeam] = useState([]);
  const [leave, setLeave] = useState([]);
  const [sick, setSick] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    try {
      const [o, t, l, s, i] = await Promise.all([API.get("/hr/overview"), API.get("/hr/team"), API.get("/hr/leave?all=1"), API.get("/hr/sickness?all=1"), API.get("/hr/invoices?all=1")]);
      setOverview(o.data); setTeam(t.data.team); setLeave(l.data.leave); setSick(s.data.sickness); setInvoices(i.data.invoices);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { if (user?.operations_director) load(); }, [load, user?.operations_director]);

  if (!user?.operations_director) {
    return (
      <div className="rounded-xl border border-border p-8 text-center space-y-2" data-testid="hr-hub-denied">
        <p className="font-semibold">Operations Directors only</p>
        <p className="text-sm text-muted-foreground">The HR Hub shows everyone's records. Your own HR portal is at <Link to="/hr" className="underline">My HR</Link>.</p>
      </div>
    );
  }
  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };

  return (
    <div className="space-y-6" data-testid="hr-hub-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><UsersThree className="h-6 w-6" weight="duotone" /> HR Hub</h1>
          <p className="text-sm text-muted-foreground mt-1">Everyone's time, leave, sickness and invoices. Approve, override and set allowances and rates here.</p>
        </div>
        <Link to="/hr" className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted" data-testid="hr-hub-my-hr"><IdentificationBadge className="h-4 w-4" /> My HR</Link>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="hr-hub-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="team" data-testid="hr-hub-tab-team">Team</TabsTrigger>
          <TabsTrigger value="leave" data-testid="hr-hub-tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="sickness" data-testid="hr-hub-tab-sickness">Sickness</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="hr-hub-tab-invoices">Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><OpsOverview data={overview} reload={load} /></TabsContent>
        <TabsContent value="team" className="mt-4"><TeamTable team={team} reload={load} /></TabsContent>
        <TabsContent value="leave" className="mt-4">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-5 items-start">
            <LeaveCalendar value={null} selectable={false} refreshKey={leave.length} />
            <div className="rounded-xl border border-border divide-y divide-border" data-testid="hr-hub-leave-list">
              <p className="px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">All leave this year</p>
              {leave.length === 0 && <p className="p-4 text-sm text-muted-foreground">No leave yet.</p>}
              {leave.map((l) => (
                <Link key={l.id} to={`/hr?user_id=${l.user_id}&tab=leave`} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-muted/40">
                  <div><p className="text-sm font-medium">{l.user_name} <span className="text-muted-foreground font-normal">· {dayRange(l.start_date, l.end_date)} · {l.days}d</span></p>{l.note && <p className="text-xs text-muted-foreground truncate">{l.note}</p>}</div>
                  <StatusPill status={l.status} />
                </Link>
              ))}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="sickness" className="mt-4">
          <div className="rounded-xl border border-border divide-y divide-border" data-testid="hr-hub-sick-list">
            {sick.length === 0 && <p className="p-4 text-sm text-muted-foreground">No sickness recorded.</p>}
            {sick.map((s) => (
              <Link key={s.id} to={`/hr?user_id=${s.user_id}&tab=sickness`} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-muted/40">
                <div><p className="text-sm font-medium">{s.user_name} <span className="text-muted-foreground font-normal">· {s.end_date ? dayRange(s.start_date, s.end_date) : `${ukDate(s.start_date)} – ongoing`} · {s.days}d</span></p><p className="text-xs text-muted-foreground truncate">{s.reason}</p></div>
                <StatusPill status={s.status} />
              </Link>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="invoices" className="mt-4">
          <div className="rounded-xl border border-border divide-y divide-border" data-testid="hr-hub-invoice-list">
            {invoices.length === 0 && <p className="p-4 text-sm text-muted-foreground">No invoices yet.</p>}
            {invoices.map((r) => (
              <Link key={r.id} to={`/hr?user_id=${r.user_id}&tab=invoices`} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-muted/40">
                <div><p className="text-sm font-medium">{r.user_name} · {r.invoice_number || "Draft"}</p><p className="text-xs text-muted-foreground">{monthLabel(r.month)}</p></div>
                <div className="flex items-center gap-2"><span className="font-semibold tabular-nums">{money(r.total)}</span><StatusPill status={r.status} /></div>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
